import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Palette, Upload, X, Save, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

async function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = () => rej(r.error);
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}

export default function BrandingPage() {
  const [s, setS] = useState({ app_name: "", app_tagline: "", app_logo: null, login_hero: null });
  const [loading, setLoading] = useState(false);
  const logoRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    api.get("/settings/branding").then(r => setS(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);

  async function onLogo(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Maks 2MB");
    const b64 = await fileToB64(f); setS({ ...s, app_logo: b64 });
  }
  async function onHero(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Maks 5MB");
    const b64 = await fileToB64(f); setS({ ...s, login_hero: b64 });
  }

  async function save() {
    setLoading(true);
    try {
      await api.put("/settings/branding", s);
      toast.success("Branding disimpan. Refresh halaman untuk lihat perubahan.");
      // Trigger a global event so sidebar & login can refresh
      window.dispatchEvent(new CustomEvent("branding-updated"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-6" data-testid="branding-page">
      <PageHeader overline="Tampilan Aplikasi" title="Branding & Tampilan" description="Atur nama aplikasi, tagline, logo sidebar, dan gambar latar halaman login." icon={Palette} tone="brand" />

      <Card className="p-5 space-y-4 border-border shadow-none">
        <p className="overline">Identitas Aplikasi</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Nama Aplikasi</Label>
            <Input value={s.app_name || ""} onChange={e=>setS({...s, app_name: e.target.value})} placeholder="HPM Integrasi" data-testid="input-app-name" />
          </div>
          <div>
            <Label>Tagline / Subtitle</Label>
            <Input value={s.app_tagline || ""} onChange={e=>setS({...s, app_tagline: e.target.value})} placeholder="Sistem Surat & Absensi" data-testid="input-app-tagline" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* App Logo */}
        <Card className="p-5 space-y-3 border-border shadow-none">
          <p className="overline flex items-center gap-1.5"><ImgIcon className="h-3 w-3" /> Logo Aplikasi (Sidebar)</p>
          <p className="text-xs text-ink-500">PNG/SVG transparan, kotak (1:1) hasil terbaik. Maks 2MB.</p>
          <div className="rounded-md border border-dashed border-border p-4 flex items-center gap-4">
            {s.app_logo ? (
              <img src={s.app_logo} alt="logo" className="h-16 w-16 rounded object-contain bg-white" />
            ) : (
              <div className="h-16 w-16 rounded bg-gradient-to-br from-brand to-brand-700 grid place-items-center text-white font-heading font-bold text-2xl">H</div>
            )}
            <div className="flex-1">
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="upload-app-logo" />
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={()=>logoRef.current?.click()} data-testid="btn-pick-app-logo"><Upload className="mr-1 h-3 w-3" /> Pilih file</Button>
                {s.app_logo && <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={()=>{ if (logoRef.current) logoRef.current.value=""; setS({...s, app_logo: null}); }} data-testid="btn-clear-app-logo"><X className="mr-1 h-3 w-3" /> Hapus</Button>}
              </div>
            </div>
          </div>
        </Card>

        {/* Login Hero */}
        <Card className="p-5 space-y-3 border-border shadow-none">
          <p className="overline flex items-center gap-1.5"><ImgIcon className="h-3 w-3" /> Gambar Halaman Login</p>
          <p className="text-xs text-ink-500">Gambar besar yang muncul di sisi kiri halaman login. JPG/PNG, lanskap (16:9). Maks 5MB.</p>
          <div className="rounded-md border border-dashed border-border p-3 space-y-3">
            {s.login_hero ? (
              <img src={s.login_hero} alt="hero" className="h-40 w-full rounded object-cover" />
            ) : (
              <div className="h-40 w-full rounded bg-gradient-to-br from-ink-900 via-ink-700 to-brand grid place-items-center text-white/50 text-sm">Belum ada gambar</div>
            )}
            <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={onHero} data-testid="upload-login-hero" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={()=>heroRef.current?.click()} data-testid="btn-pick-login-hero"><Upload className="mr-1 h-3 w-3" /> Pilih file</Button>
              {s.login_hero && <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={()=>{ if (heroRef.current) heroRef.current.value=""; setS({...s, login_hero: null}); }} data-testid="btn-clear-login-hero"><X className="mr-1 h-3 w-3" /> Hapus</Button>}
            </div>
          </div>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={loading} data-testid="btn-save-branding"><Save className="mr-2 h-4 w-4" /> {loading ? "Menyimpan…" : "Simpan Branding"}</Button>
      </div>
    </div>
  );
}
